/**
 * The portal session id sent as `X-Session-Id`, and its restart after the API
 * expires it. Policy: docs/architecture/context.md "Portal session".
 */

export const SESSION_HEADER = "X-Session-Id";

let sessionId: string | null = null;
let startNewSession: (() => Promise<void>) | null = null;
let inFlightRestart: Promise<void> | null = null;

/** Sets the id sent on every later call; `null` sends none. */
export function setSessionId(id: string | null) {
  sessionId = id;
}

/** Registers how to start a new session. Returns an unregister function. */
export function onSessionExpired(start: () => Promise<void>): () => void {
  startNewSession = start;
  return () => {
    if (startNewSession === start) startNewSession = null;
  };
}

/** The id to send, once any restart in progress has settled. */
export async function currentSessionId(): Promise<string | null> {
  await inFlightRestart?.catch(() => undefined);
  return sessionId;
}

/** True once a new session replaces `expiredId`; concurrent callers share one restart. */
export async function restartExpiredSession(expiredId: string): Promise<boolean> {
  if (sessionId === expiredId) {
    sessionId = null;
    if (!startNewSession) return false;
    inFlightRestart = startNewSession().finally(() => {
      inFlightRestart = null;
    });
  }
  try {
    await inFlightRestart;
  } catch {
    return false;
  }
  return sessionId !== null && sessionId !== expiredId;
}
